import { create } from "zustand";
import { normalizeApiError } from "../lib/apiErrors";

// Global toast state (Zustand, not persisted). Any surface can push a toast via
// the helpers below without a provider: toastSuccess / toastApiError / etc.
// Rendered once by <Toaster /> at the app root.

export type ToastSeverity = "success" | "error" | "warning" | "info";

export interface Toast {
  id: number;
  severity: ToastSeverity;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (severity: ToastSeverity, message: string) => void;
  dismiss: (id: number) => void;
}

const MAX_TOASTS = 3;
const AUTO_DISMISS_MS = 6000;

let nextId = 1;

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  push: (severity, message) => {
    const id = nextId++;
    set((state) => ({
      toasts: [...state.toasts.slice(-(MAX_TOASTS - 1)), { id, severity, message }],
    }));
    window.setTimeout(() => useToastStore.getState().dismiss(id), AUTO_DISMISS_MS);
  },
  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

export const toastSuccess = (message: string): void => {
  useToastStore.getState().push("success", message);
};

export const toastInfo = (message: string): void => {
  useToastStore.getState().push("info", message);
};

export const toastWarning = (message: string): void => {
  useToastStore.getState().push("warning", message);
};

export const toastError = (message: string): void => {
  useToastStore.getState().push("error", message);
};

// Normalize any backend failure and toast it. Auth denials surface as warnings,
// everything else as errors. Returns the normalized error for inline handling.
export const toastApiError = (error: unknown, fallback?: string) => {
  const normalized = normalizeApiError(error, fallback);
  const severity: ToastSeverity =
    normalized.kind === "unauthorized" || normalized.kind === "forbidden"
      ? "warning"
      : "error";
  useToastStore.getState().push(severity, normalized.message);
  return normalized;
};
